import { useState, useRef, useEffect } from 'react';
import {
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentTextIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useTrainingUser } from './TrainingContext';
import { StandardHeader } from '../../components/common/StandardHeader';
import MetricCard from '../../components/common/MetricCard';
import { SOPS_INIT, ASSIGNMENTS_INIT, STATUS_CFG, MOCK_TRAINING_USERS } from './data/mockData';

// ── User Switcher ────────────
function UserSwitcher() {
  const { trainingUser, setTrainingUser } = useTrainingUser();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const initials = trainingUser?.name
    ? trainingUser.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-grey-100 transition-colors focus:outline-none"
      >
        <div className="w-7 h-7 rounded-full bg-darkblue-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
          {initials}
        </div>
        <span className="hidden sm:block text-sm font-medium text-darkblue-500 max-w-[110px] truncate">
          {trainingUser?.name?.split(' ')[0] ?? 'User'}
        </span>
        <span className={`hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
          trainingUser?.role === 'admin'
            ? 'bg-brand-50 text-brand-600'
            : 'bg-grey-100 text-grey-500'
        }`}>
          {trainingUser?.role ?? 'user'}
        </span>
        <ChevronDownIcon className={`w-3.5 h-3.5 text-grey-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[98]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-grey-200 rounded-xl shadow-lg z-[99] overflow-hidden">
            <div className="px-3 py-2 border-b border-grey-100 bg-grey-50">
              <p className="text-[10px] font-bold uppercase tracking-widest text-grey-400">Switch User</p>
            </div>
            <div className="py-1">
              {MOCK_TRAINING_USERS.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { setTrainingUser(u); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-grey-50 ${
                    trainingUser?.id === u.id ? 'bg-brightblue-50' : ''
                  }`}
                >
                  <div className="w-7 h-7 rounded-full bg-darkblue-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                    {u.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-darkblue-500 truncate">{u.name}</p>
                    <p className="text-[10px] text-grey-400 truncate">{u.email}</p>
                  </div>
                  <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                    u.role === 'admin'
                      ? 'bg-brand-50 text-brand-600'
                      : 'bg-grey-100 text-grey-500'
                  }`}>
                    {u.role}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────
// STATUS_CFG should use Pulse tokens. If your mockData still has
// old Tailwind colors, override here:
const STATUS_STYLES = {
  not_started: { label: 'Not Started', bg: 'bg-grey-50',     border: 'border-grey-200',   text: 'text-grey-500'     },
  in_progress:  { label: 'In Progress', bg: 'bg-yellow-50',   border: 'border-yellow-200', text: 'text-yellow-700'   },
  completed:    { label: 'Completed',   bg: 'bg-teal-50',     border: 'border-teal-200',   text: 'text-teal-700'     },
};

function StatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] ?? STATUS_STYLES.not_started;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

// ── Assign Modal ──────────────────────────────────────────────
function AssignModal({ sops, assignments, preselectedSopId, onClose, onSave }) {
  const [form, setForm] = useState({ userId: '', sopId: preselectedSopId || '', dueDate: '' });
  const learners = MOCK_TRAINING_USERS.filter((u) => u.role === 'learner');
  const alreadyAssigned = assignments.find((a) => a.userId === form.userId && a.sopId === form.sopId);
  const canSave = form.userId && form.sopId && form.dueDate && !alreadyAssigned;

  const handleSave = () => {
    if (!canSave) return;
    const d = new Date(form.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    onSave({ id: `a${Date.now()}`, ...form, assignedAt: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), dueDate: d, status: 'not_started' });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200 bg-grey-50 rounded-t-xl">
          <h3 className="text-base font-semibold text-darkblue-500">New Assignment</h3>
          <button onClick={onClose} className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
              Team Member <span className="text-pink-500">*</span>
            </label>
            <select
              value={form.userId}
              onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
              className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
            >
              <option value="">Select team member...</option>
              {learners.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.title}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
              SOP <span className="text-pink-500">*</span>
            </label>
            <select
              value={form.sopId}
              onChange={(e) => setForm((f) => ({ ...f, sopId: e.target.value }))}
              className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
            >
              <option value="">Select SOP...</option>
              {sops.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
              Due Date <span className="text-pink-500">*</span>
            </label>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
              className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
            />
          </div>

          {alreadyAssigned && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-700">
              ⚠️ This SOP is already assigned to this team member
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-grey-200 bg-grey-50 rounded-b-xl flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-grey-300 text-grey-600 rounded-lg text-sm font-medium hover:bg-grey-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors disabled:opacity-40"
          >
            Assign SOP
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function Assignments({ showAssignModal, setShowAssignModal, preselectedSopId, setPreselectedSopId, sops = SOPS_INIT }) {
  const { trainingUser } = useTrainingUser();
  const isAdmin = trainingUser.role === 'admin';

  const [assignments, setAssignments] = useState(ASSIGNMENTS_INIT);

  const getUser = (id) => MOCK_TRAINING_USERS.find((u) => u.id === id);
  const getSop  = (id) => sops.find((s) => s.id === id);

  const myAssignments = assignments.filter((a) => a.userId === trainingUser.id);

  const updateStatus   = (id, status) => setAssignments((p) => p.map((a) => a.id === id ? { ...a, status } : a));
  const removeAssignment = (id) => setAssignments((p) => p.filter((a) => a.id !== id));
  const addAssignment  = (a) => setAssignments((p) => [...p, a]);

  useEffect(() => {
    document.title = 'Pulse: Assignments';
  }, []);

  // ── Stat cards using MetricCard ────────────────────────────
  const adminStats = [
    { title: 'Total Assignments', value: assignments.length,                                           icon: ClipboardDocumentListIcon, changeLabel: 'All team assignments'   },
    { title: 'In Progress',       value: assignments.filter((a) => a.status === 'in_progress').length, icon: ArrowPathIcon,             changeLabel: 'Currently active'       },
    { title: 'Completed',         value: assignments.filter((a) => a.status === 'completed').length,   icon: CheckCircleIcon,           changeLabel: 'Finished assignments'   },
  ];
  const learnerStats = [
    { title: 'To Do',       value: myAssignments.filter((a) => a.status === 'not_started').length, icon: ClockIcon,                changeLabel: 'Not yet started'   },
    { title: 'In Progress', value: myAssignments.filter((a) => a.status === 'in_progress').length, icon: ArrowPathIcon,            changeLabel: 'Currently active'  },
    { title: 'Completed',   value: myAssignments.filter((a) => a.status === 'completed').length,   icon: CheckCircleIcon,          changeLabel: 'Finished'          },
  ];
  const stats = isAdmin ? adminStats : learnerStats;

  return (
    <div className="min-h-screen bg-grey-50">
      <div className="max-w-full mx-auto space-y-4">

        <StandardHeader
          title="Assignments"
          subtitle={isAdmin ? 'Manage and track team training assignments' : 'Your assigned SOPs and training materials'}
          isRefreshing={false}
          customControls={
            <div className="flex items-center gap-3">
              <UserSwitcher />
            </div>
          }
        />

        {/* ── Stat cards ── */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {stats.map((s) => (
            <MetricCard
              key={s.title}
              title={s.title}
              value={s.value.toString()}
              icon={s.icon}
              change={null}
              changeLabel={s.changeLabel}
            />
          ))}
        </div>

        {/* ── ADMIN VIEW ── */}
        {isAdmin && (
          <>
            <div className="flex justify-end">
              <button
                onClick={() => setShowAssignModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-darkblue-600 transition-colors"
              >
                <PlusIcon className="w-4 h-4" /> New Assignment
              </button>
            </div>

            <div className="bg-white border border-grey-200 rounded-xl overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-darkblue-500 border-b border-grey-200">
                    {['Team Member', 'SOP', 'Assigned', 'Due Date', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-widest">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-grey-100">
                  {assignments.map((a) => {
                    const u = getUser(a.userId);
                    const s = getSop(a.sopId);
                    return (
                      <tr key={a.id} className="hover:bg-grey-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-darkblue-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                              {u?.initials ?? u?.name?.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div className="text-sm font-semibold">{u?.name}</div>
                              <div className="text-xs text-grey-800">{u?.title}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-darkblue-500">{s?.title}</div>
                          <div className="text-xs text-grey-800">{s?.type} · {s?.pages}p</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-grey-800">{a.assignedAt}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-darkblue-500">{a.dueDate}</td>
                        <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateStatus(a.id, a.status === 'completed' ? 'not_started' : 'completed')}
                              className="flex items-center gap-1 px-2.5 py-1 border border-grey-200 text-grey-600 rounded-lg text-xs font-medium hover:bg-grey-50 transition-colors"
                            >
                              {a.status === 'completed'
                                ? <><ArrowPathIcon className="w-3 h-3" /> Reopen</>
                                : <><CheckCircleIcon className="w-3 h-3" /> Mark Done</>}
                            </button>
                            <button
                              onClick={() => removeAssignment(a.id)}
                              className="px-2.5 py-1 border border-pink-200 bg-pink-50 text-pink-600 rounded-lg text-xs font-medium hover:bg-pink-100 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── LEARNER VIEW ── */}
        {!isAdmin && (
          myAssignments.length === 0 ? (
            <div className="text-center py-20">
              <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-grey-300" />
              <p className="font-semibold text-grey-600 mb-1">No assignments yet</p>
              <p className="text-sm text-grey-400">Your coach will assign SOPs to you soon</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myAssignments.map((a) => {
                const s = getSop(a.sopId);
                const nextStatus = a.status === 'not_started' ? 'in_progress' : a.status === 'in_progress' ? 'completed' : 'not_started';
                const btnLabel   = a.status === 'not_started' ? 'Start' : a.status === 'in_progress' ? 'Mark Complete' : 'Redo';
                return (
                  <div key={a.id} className="bg-white border border-grey-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
                    <div className="w-11 h-11 rounded-xl bg-darkblue-500 flex items-center justify-center flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-darkblue-500 mb-0.5">{s?.title}</div>
                      <div className="text-xs text-grey-400">Due: {a.dueDate} · {s?.type} · {s?.pages} pages</div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {s?.tags.map((t) => (
                          <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-brand-50 border-brand-200 text-brand-600">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <StatusBadge status={a.status} />
                      <button
                        onClick={() => updateStatus(a.id, nextStatus)}
                        className="px-3 py-1.5 border border-grey-200 text-grey-600 rounded-lg text-xs font-medium hover:bg-grey-50 transition-colors whitespace-nowrap"
                      >
                        {btnLabel}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {showAssignModal && (
        <AssignModal
          sops={sops}
          assignments={assignments}
          preselectedSopId={preselectedSopId}
          onClose={() => { setShowAssignModal(false); setPreselectedSopId(null); }}
          onSave={addAssignment}
        />
      )}
    </div>
  );
}